import { t } from '@/features/i18n/utils';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Header, HeaderButton, HeaderOrganizationMenu } from '@/ui/components/header';
import { Breadcrumb, BreadcrumbPage } from '@/ui/components/breadcrumb';
import { MainHeader, MainScrollArea } from '@/ui/components/main';
import { Icon } from '@/ui/components/icon';
import { InboxIcon, TickDouble02Icon } from '@hugeicons-pro/core-stroke-rounded';
import z from 'zod';
import { NOTIFICATION_TABS } from '@/features/notifications/constants';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useInfiniteScroll } from '@/ui/hooks/use-infinite-scroll';
import { notificationsQuery, unreadCountQuery } from '@/features/notifications/queries';
import { Button } from '@/ui/components/button';
import { useMarkNotificationsRead } from '@/features/notifications/mutations';
import { H1 } from '@/ui/components/heading';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyMedia,
  EmptyTitle,
} from '@/ui/components/empty';
import { Spinner } from '@/ui/components/spinner';
import { Separator } from '@/ui/components/separator';
import { NotificationItem } from '@/features/notifications/components/notification-item';
import { Tabs, TabsList, TabsTab } from '@/ui/components/tabs';
import { Switch } from '@/ui/components/switch';

export const Route = createFileRoute('/_authed/_organization/notifications')({
  component: RouteComponent,
  validateSearch: z.object({
    showUnreads: z.boolean().optional(),
    tab: z.enum(NOTIFICATION_TABS).catch('all'),
  }),
});

function RouteComponent() {
  const search = Route.useSearch();
  const navigate = useNavigate();

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery(
    notificationsQuery(search.showUnreads ? false : undefined),
  );
  const { data: unreadData } = useQuery(unreadCountQuery());
  const markNotificationsRead = useMarkNotificationsRead();

  const scrollRef = useInfiniteScroll<HTMLDivElement>({
    onLoadMore: fetchNextPage,
    hasMore: hasNextPage,
    isLoading: isFetchingNextPage,
  });

  const notifications = data?.pages.flatMap((p) => p.data) ?? [];
  const visibleNotifications =
    search.tab === 'mentions'
      ? notifications.filter((n) => n.type === 'comment_tag')
      : notifications;

  const unreadCount = unreadData?.unreadCount ?? 0;

  return (
    <>
      <Header>
        <HeaderOrganizationMenu />
        <Breadcrumb>
          <BreadcrumbPage>
            <Icon icon={InboxIcon} />
            {t({ en: 'Notifications', nb: 'Notifikasjoner' })}
          </BreadcrumbPage>
        </Breadcrumb>
        {unreadCount > 0 && (
          <HeaderButton
            aria-label={t({ en: 'Mark all as read', nb: 'Merk alle som lest' })}
            variant="ghost"
            onClick={() => markNotificationsRead.mutate({})}
          >
            <Icon icon={TickDouble02Icon} />
          </HeaderButton>
        )}
      </Header>
      <MainScrollArea viewportRef={scrollRef}>
        <MainHeader className="max-md:hidden">
          <H1>{t({ en: 'Notifications', nb: 'Notifikasjoner' })}</H1>
          {unreadCount > 0 && (
            <Button
              variant="secondary"
              kind="with-icon"
              onClick={() => markNotificationsRead.mutate({})}
            >
              <Icon icon={TickDouble02Icon} />
              {t({ en: 'Mark all as read', nb: 'Merk alle som lest' })}
            </Button>
          )}
        </MainHeader>
        <div className="relative">
          <Tabs
            value={search.tab}
            onValueChange={(tab) => {
              navigate({ to: '.', replace: true, search: (prev) => ({ ...prev, tab }) });
            }}
          >
            <TabsList containerClassName="pl-3">
              <TabsTab value="all">
                {t({ en: 'All', nb: 'Alle' })}
                {unreadCount > 0 ? ` (${unreadCount})` : ''}
              </TabsTab>
              <TabsTab value="mentions">{t({ en: '@ Mentions', nb: '@ Nevninger' })}</TabsTab>
            </TabsList>
          </Tabs>
          <div className="absolute right-3.5 inset-y-0 flex items-center gap-2 text-sm text-muted">
            <span>{t({ en: 'Show unreads', nb: 'Vis uleste' })}</span>
            <Switch
              size="sm"
              checked={!!search.showUnreads}
              onCheckedChange={(checked) => {
                navigate({
                  to: '.',
                  search: (prev) => ({ ...prev, showUnreads: checked }),
                });
              }}
            />
          </div>
        </div>
        {isLoading ? (
          <Spinner kind="inset" />
        ) : visibleNotifications.length === 0 ? (
          <Empty>
            <EmptyMedia variant="icon">
              <Icon icon={InboxIcon} />
            </EmptyMedia>
            <EmptyContent>
              <EmptyTitle>
                {t({ en: 'No notifications to display', nb: 'Ingen varsler å vise' })}
              </EmptyTitle>
              <EmptyDescription>
                {t({
                  en: 'There are no notifications that match your current filters',
                  nb: 'Det er ingen varsler som samsvarer med gjeldende filtre',
                })}
              </EmptyDescription>
            </EmptyContent>
            {(search.tab !== 'all' || search.showUnreads) && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() =>
                  navigate({
                    to: '.',
                    search: (prev) => ({ ...prev, tab: 'all', showUnreads: false }),
                  })
                }
              >
                {t({ en: 'Clear filters', nb: 'Fjern filtre' })}
              </Button>
            )}
          </Empty>
        ) : (
          <>
            {visibleNotifications.map((notification, index) => (
              <div key={notification.id}>
                {index > 0 && <Separator />}
                <NotificationItem notification={notification} />
              </div>
            ))}
            {isFetchingNextPage ? (
              <div className="flex justify-center py-3">
                <Spinner />
              </div>
            ) : null}
          </>
        )}
      </MainScrollArea>
    </>
  );
}
